import { useState, useEffect } from 'react';
import { Check, Zap, Star, Building2, Rocket, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useWorkspaceStore } from '../stores/workspace.store';
import { useAuthStore } from '../stores/auth.store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceApi } from '../services/workspace.service';
import { abacatePayApi } from '../services/abacatepay.service';
import { useToast } from '../hooks/use-toast';

// ─── Plan definitions ─────────────────────────────────────────────────────────

type BillingCycle = 'monthly' | 'yearly';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  priceMonthly: number | null;
  priceYearly: number | null;
  highlight: boolean;
  badge?: string;
  cta: string;
  features: PlanFeature[];
}

const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Para experimentar e projetos pessoais simples.',
    icon: <Rocket className="h-5 w-5" />,
    priceMonthly: 0,
    priceYearly: 0,
    highlight: false,
    cta: 'Plano atual',
    features: [
      { text: '1 página', included: true },
      { text: '5 componentes', included: true },
      { text: '1 componente de dados', included: true },
      { text: '1 integração', included: true },
      { text: '1 membro por workspace', included: true },
      { text: 'Múltiplas páginas', included: false },
      { text: 'Suporte prioritário', included: false },
      { text: 'Personalização avançada', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Profissional',
    description: 'Para times pequenos e projetos em crescimento.',
    icon: <Zap className="h-5 w-5" />,
    priceMonthly: 49,
    priceYearly: 39,
    highlight: false,
    cta: 'Assinar Profissional',
    features: [
      { text: '3 páginas', included: true },
      { text: '7 componentes por página', included: true },
      { text: '1 componente de dados por página', included: true },
      { text: '3 integrações', included: true },
      { text: 'Até 5 membros por workspace', included: true },
      { text: 'Suporte prioritário', included: false },
      { text: 'Personalização avançada', included: false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Poder total, sem limites.',
    icon: <Star className="h-5 w-5" />,
    priceMonthly: 99,
    priceYearly: 79,
    highlight: true,
    badge: 'Mais popular',
    cta: 'Assinar Premium',
    features: [
      { text: '10 páginas', included: true },
      { text: '10 componentes por página', included: true },
      { text: '3 componentes de dados', included: true },
      { text: 'Integrações ilimitadas', included: true },
      { text: 'Membros ilimitados', included: true },
      { text: 'Suporte prioritário', included: true },
      { text: 'Exportação de dados', included: true },
      { text: 'Personalização avançada', included: false },
      { text: 'SLA garantido', included: false },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Soluções customizadas para grandes operações.',
    icon: <Building2 className="h-5 w-5" />,
    priceMonthly: null,
    priceYearly: null,
    highlight: false,
    cta: 'Falar com vendas',
    features: [
      { text: 'Tudo do Premium', included: true },
      { text: 'Limite de componentes a negociar', included: true },
      { text: 'Customizações sob demanda', included: true },
      { text: 'Integração personalizada', included: true },
      { text: 'Membros ilimitados', included: true },
      { text: 'Suporte dedicado 24/7', included: true },
      { text: 'SLA garantido', included: true },
      { text: 'Ambiente isolado (on-premise)', included: true },
      { text: 'Treinamento da equipe', included: true },
    ],
  },
];

// ─── Feature comparison table ─────────────────────────────────────────────────

const COMPARISON_ROWS = [
  { label: 'Páginas', values: ['1', '3', '10', 'A negociar'] },
  { label: 'Componentes por página', values: ['5', '7', '10', 'A negociar'] },
  { label: 'Componentes de dados por página', values: ['1', '1', '3', 'A negociar'] },
  { label: 'Integrações', values: ['1', '3', 'Ilimitado', 'A negociar'] },
  { label: 'Membros', values: ['1', '5', 'Ilimitado', 'Ilimitado'] },
  { label: 'Suporte prioritário', values: [false, false, true, true] },
  { label: 'Personalização avançada', values: [false, false, false, true] },
  { label: 'Customizações sob demanda', values: [false, false, false, true] },
  { label: 'SLA garantido', values: [false, false, false, true] },
];

// ─── Checkout modal ───────────────────────────────────────────────────────────

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  plan: Plan;
  billingCycle: BillingCycle;
  workspaceId: string;
  defaultEmail: string;
  defaultName: string;
}

function CheckoutModal({
  open,
  onClose,
  plan,
  billingCycle,
  workspaceId,
  defaultEmail,
  defaultName,
}: CheckoutModalProps) {
  const { toast } = useToast();
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [taxId, setTaxId] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setEmail(defaultEmail);
      setTaxId('');
      setCellphone('');
    }
  }, [open, defaultName, defaultEmail]);

  const price = billingCycle === 'monthly' ? plan.priceMonthly! : plan.priceYearly!;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { url } = await abacatePayApi.createCheckout({
        workspaceId,
        planId: plan.id,
        billingCycle,
        customerName: name.trim(),
        customerEmail: email.trim(),
        customerTaxId: taxId.trim(),
        customerCellphone: cellphone.trim(),
      });
      window.location.href = url;
    } catch (err: any) {
      toast({
        title: 'Erro ao gerar cobrança',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assinar {plan.name}</DialogTitle>
          <DialogDescription>
            R$ {price}/mês — cobrança via {billingCycle === 'yearly' ? `PIX anual (R$ ${price * 12})` : 'PIX mensal'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="co-name">Nome completo</Label>
            <Input
              id="co-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="co-email">E-mail</Label>
            <Input
              id="co-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="co-taxid">CPF</Label>
            <Input
              id="co-taxid"
              placeholder="000.000.000-00"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="co-phone">WhatsApp / Celular</Label>
            <Input
              id="co-phone"
              placeholder="+55 11 99999-9999"
              value={cellphone}
              onChange={(e) => setCellphone(e.target.value)}
              required
            />
          </div>

          <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground space-y-1">
            <p>🔒 Pagamento processado com segurança pela AbacatePay (PIX)</p>
            <p>💡 Você será redirecionado para a tela de pagamento</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ir para pagamento
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PricingPage() {
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const { currentWorkspace } = useWorkspaceStore();
  const { user } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Detect payment success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      toast({ title: 'Pagamento confirmado! Seu plano será atualizado em breve.' });
      // Clean query param without navigation
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  // Fetch current workspace plan
  const { data: workspaceDetails } = useQuery({
    queryKey: ['workspace', currentWorkspace?.id],
    queryFn: () => workspaceApi.getOne(currentWorkspace!.id),
    enabled: !!currentWorkspace,
  });

  const currentPlan = (workspaceDetails as any)?.plan ?? 'free';

  const getPrice = (plan: Plan) => {
    if (plan.priceMonthly === null) return null;
    return billing === 'monthly' ? plan.priceMonthly : plan.priceYearly!;
  };

  const handleCta = (plan: Plan) => {
    if (plan.id === 'free' || plan.id === currentPlan) return;
    if (plan.id === 'enterprise') {
      window.open(
        'mailto:vendas@rheoly.com?subject=Enterprise%20-%20' + (currentWorkspace?.name ?? ''),
        '_blank',
      );
      return;
    }
    setCheckoutPlan(plan);
  };

  const handleCancel = async () => {
    if (!currentWorkspace) return;
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? O workspace voltará para o plano Free.')) return;
    setCancelLoading(true);
    try {
      await abacatePayApi.cancelSubscription(currentWorkspace.id);
      toast({ title: 'Assinatura cancelada. Seu plano foi revertido para Free.' });
      queryClient.invalidateQueries({ queryKey: ['workspace', currentWorkspace.id] });
    } catch (err: any) {
      toast({
        title: 'Erro ao cancelar assinatura',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      });
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-12">
      {checkoutPlan && currentWorkspace && (
        <CheckoutModal
          open={!!checkoutPlan}
          onClose={() => setCheckoutPlan(null)}
          plan={checkoutPlan}
          billingCycle={billing}
          workspaceId={currentWorkspace.id}
          defaultEmail={user?.email ?? ''}
          defaultName={user?.name ?? ''}
        />
      )}

      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold">Planos e preços</h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Escolha o plano ideal para o seu time. Faça upgrade ou downgrade a qualquer momento.
        </p>

        {/* Billing toggle */}
        <div className="inline-flex items-center gap-1 rounded-full border p-1 bg-muted text-sm">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-4 py-1.5 rounded-full font-medium transition-all ${
              billing === 'monthly'
                ? 'bg-background shadow text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Mensal
          </button>
          <button
            onClick={() => setBilling('yearly')}
            className={`px-4 py-1.5 rounded-full font-medium transition-all flex items-center gap-2 ${
              billing === 'yearly'
                ? 'bg-background shadow text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Anual
            <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 px-1.5 py-0.5 rounded-full font-semibold">
              -20%
            </span>
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.map((plan) => {
          const price = getPrice(plan);
          const isCurrentPlan = plan.id === currentPlan;

          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl border flex flex-col p-6 transition-shadow ${
                plan.highlight
                  ? 'border-primary shadow-lg shadow-primary/10 bg-primary/[0.03]'
                  : 'border-border bg-card'
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Icon + name */}
              <div className="flex items-center gap-2 mb-3">
                <div
                  className={`p-2 rounded-lg ${
                    plan.highlight
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {plan.icon}
                </div>
                <h2 className="font-semibold text-base">{plan.name}</h2>
              </div>

              <p className="text-sm text-muted-foreground mb-5 flex-none">
                {plan.description}
              </p>

              {/* Price */}
              <div className="mb-6">
                {price === null ? (
                  <p className="text-2xl font-bold">Sob consulta</p>
                ) : price === 0 ? (
                  <p className="text-2xl font-bold">Grátis</p>
                ) : (
                  <div className="flex items-end gap-1">
                    <span className="text-sm text-muted-foreground">R$</span>
                    <span className="text-3xl font-bold">{price}</span>
                    <span className="text-sm text-muted-foreground mb-0.5">/mês</span>
                  </div>
                )}
                {billing === 'yearly' && price !== null && price > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Cobrado anualmente — R$ {price * 12}/ano
                  </p>
                )}
              </div>

              {/* CTA */}
              <Button
                className="w-full mb-2"
                variant={plan.highlight ? 'default' : isCurrentPlan ? 'outline' : 'outline'}
                disabled={isCurrentPlan}
                onClick={() => handleCta(plan)}
              >
                {isCurrentPlan ? 'Plano atual' : plan.cta}
              </Button>

              {/* Cancel subscription — shown only for active paid plans */}
              {isCurrentPlan && plan.id !== 'free' && plan.id !== 'enterprise' && (
                <button
                  className="w-full mb-4 text-xs text-muted-foreground hover:text-destructive transition-colors py-1"
                  onClick={handleCancel}
                  disabled={cancelLoading}
                >
                  {cancelLoading
                    ? 'Cancelando...'
                    : 'Cancelar assinatura'}
                </button>
              )}

              {/* Spacer when no cancel button */}
              {!(isCurrentPlan && plan.id !== 'free' && plan.id !== 'enterprise') && (
                <div className="mb-6" />
              )}

              {/* Features */}
              <ul className="space-y-2.5 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature.text} className="flex items-start gap-2 text-sm">
                    <Check
                      className={`h-4 w-4 mt-0.5 shrink-0 ${
                        feature.included ? 'text-primary' : 'text-muted-foreground/30'
                      }`}
                    />
                    <span
                      className={feature.included ? '' : 'text-muted-foreground/50 line-through'}
                    >
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Comparação detalhada</h2>
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-1/3">
                  Recurso
                </th>
                {PLANS.map((p) => (
                  <th key={p.id} className="text-center px-4 py-3 font-semibold">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr key={row.label} className={i % 2 === 0 ? '' : 'bg-muted/20'}>
                  <td className="px-4 py-3 text-muted-foreground">{row.label}</td>
                  {row.values.map((val, j) => (
                    <td key={j} className="px-4 py-3 text-center">
                      {typeof val === 'boolean' ? (
                        val ? (
                          <Check className="h-4 w-4 text-primary mx-auto" />
                        ) : (
                          <span className="text-muted-foreground/30 text-lg leading-none">—</span>
                        )
                      ) : (
                        <span className="font-medium">{val}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Perguntas frequentes</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            {
              q: 'Posso mudar de plano a qualquer momento?',
              a: 'Sim. O upgrade é imediato e o downgrade entra em vigor no próximo ciclo de cobrança.',
            },
            {
              q: 'O que acontece se eu atingir o limite do Free?',
              a: 'Você ainda pode acessar o que já criou, mas precisará fazer upgrade para criar mais páginas ou componentes.',
            },
            {
              q: 'Quais formas de pagamento são aceitas?',
              a: 'Cartão de crédito, boleto bancário e Pix. Todos processados de forma segura.',
            },
            {
              q: 'Existe contrato de fidelidade?',
              a: 'Não. Os planos mensais podem ser cancelados a qualquer momento. Os anuais têm desconto mas também podem ser cancelados.',
            },
          ].map((item) => (
            <div key={item.q} className="rounded-xl border bg-card p-5 space-y-2">
              <p className="font-medium text-sm">{item.q}</p>
              <p className="text-sm text-muted-foreground">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="rounded-2xl border bg-muted/30 p-8 text-center space-y-4">
        <Building2 className="h-8 w-8 mx-auto text-muted-foreground" />
        <h2 className="text-lg font-semibold">Precisa de algo maior?</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          O plano Enterprise oferece customizações sob demanda, SLA garantido e suporte dedicado.
          Fale com nosso time.
        </p>
        <Button
          variant="outline"
          onClick={() =>
            window.open(
              'mailto:vendas@rheoly.com?subject=Enterprise%20-%20' + (currentWorkspace?.name ?? ''),
              '_blank',
            )
          }
        >
          Falar com vendas
        </Button>
      </section>
    </div>
  );
}
